import { startOfDay } from 'date-fns';
import * as vp from 'vopay';
import * as Yup from 'yup';
import { roundCurrency, isElinxActive, isFutureScheduled, IS_SCHEDULING_SUPPORTED_ON_ASP } from '../utils';
import { ElinxFieldId, PaymentInfoAndUpdate, RenderedOptions } from '../types';
import { ElinxRenderBag, ElinxRow } from '../Elinx/useElinxRows';
import RemainingAmount from '../RemainingAmount';
import styles from '../AccountingSystemOpen.module.scss';

import { JSX } from 'react';
import type { TFunction } from 'i18next';

import { SelectOptionLocked } from '#shared/components/UI';

export type BuildPaymentUpdateRowsProps = {
  country: string;
  financialInstitutions: RenderedOptions[];
  branchTransitNumbers: RenderedOptions[];
  type: 'Payable' | 'Receivable';
  totalAmount: number;
  currency: string;
  creditNotes: vp.CreditNote[];
  // Root (unprefixed) t: this is a builder, not a hook, so it uses full keys.
  t: TFunction;
  tElinx: TFunction;
  elinxRows: ElinxRow[];
  canUseElinx: boolean;
};

export type PaymentUpdateRows = {
  label?: vp.PaymentUpdateLabel | vp.PaymentInfoLabel | string;
  subtitle?: string;
  id?: vp.PaymentUpdateId | vp.PaymentInfoId | ElinxFieldId;
  require: (ctx: PaymentInfoAndUpdate) => boolean;
  visible: (ctx: PaymentInfoAndUpdate) => boolean;
  validate?: (ctx: PaymentInfoAndUpdate) => unknown;
  getLeftElement?: (ctx: PaymentInfoAndUpdate) => JSX.Element;
  getRightElement?: (ctx: PaymentInfoAndUpdate) => JSX.Element;
  disabled?: (ctx: PaymentInfoAndUpdate) => boolean;
  preset?: keyof vp.TextFieldPresets | undefined;
  type?:
    | 'input'
    | 'select'
    | 'selectFinanacialInstitution'
    | 'googleAddress'
    | 'radio'
    | 'date'
    | 'multiSelect'
    | 'checkbox'
    | 'elinx'
    | 'elinxSelect'
    | 'elinxMultiSelect';
  placeholder?: string;
  disabledMessage?: string;
  filterable?: boolean;
  getSelectOptions?: (ctx: PaymentInfoAndUpdate) => RenderedOptions[];
  getElinxOptions?: (ctx: PaymentInfoAndUpdate) => RenderedOptions[];
  render?: (bag: ElinxRenderBag) => React.ReactNode;
  renderOption?: (o: RenderedOptions, args: vp.SelectRenderOptionArgs) => React.ReactNode;
  getRadioOptions?: (ctx: PaymentInfoAndUpdate) => Record<string, string>;
  container: 'field' | 'wrapper' | 'inputs';
  showValue?: boolean;
  paymentRows: PaymentUpdateRows[];
  min?: number;
  inputWidth?: string;
};

export const buildPaymentUpdateRows = ({
  type,
  financialInstitutions,
  branchTransitNumbers,
  country,
  totalAmount,
  currency,
  creditNotes,
  t,
  tElinx,
  elinxRows,
  canUseElinx,
}: BuildPaymentUpdateRowsProps): PaymentUpdateRows[] => {
  const achLabel = t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.achBankAccount', {
    defaultValue: 'Automated Clearing House (Bank Account)',
  });

  const eftLabel = t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.eftBankAccount', {
    defaultValue: 'Electronic Funds Transfer (Bank Account)',
  });

  const PaymentOptionsReceivablesUS: RenderedOptions[] = [
    { label: achLabel, value: 'Bank Account', renderOption: achLabel },
  ];

  const PaymentOptionsPayablesUS: RenderedOptions[] = [
    { label: achLabel, value: 'Bank Account', renderOption: achLabel },
    { label: 'RTP', value: 'RTP', renderOption: 'RTP' },
  ];

  const PaymentOptionsReceivables: RenderedOptions[] = [
    { label: eftLabel, value: 'Bank Account', renderOption: eftLabel },
    {
      label: 'Interac e-Transfer® Request Money',
      value: 'Interac e-Transfer Money Request',
      renderOption: <vp.InteracWordmark type="moneyRequest" />,
    },
    {
      label: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.creditCard', { defaultValue: 'Credit Card' }),
      value: 'Credit Card',
      renderOption: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.creditCard', {
        defaultValue: 'Credit Card',
      }),
    },
  ];

  const PaymentOptionsPayables: RenderedOptions[] = [
    { label: eftLabel, value: 'Bank Account', renderOption: eftLabel },
    // label/renderOption are display only - value stays the raw PaymentMethod the API expects.
    { label: 'VoPay Instant™', value: 'VoPay Instant', renderOption: 'VoPay Instant™' },
    {
      label: 'Interac e-Transfer',
      value: 'Interac e-Transfer',
      renderOption: <vp.InteracWordmark />,
    },
    {
      label: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.debitCard', { defaultValue: 'Debit Card' }),
      value: 'Debit Card',
      renderOption: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.debitCard', {
        defaultValue: 'Debit Card',
      }),
    },
  ];

  const InteracMethodTypes: RenderedOptions[] = [
    {
      label: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.emailAddress', { defaultValue: 'Email Address' }),
      value: 'Email Address',
    },
    {
      label: t('Shared.AccountingSystem.OpenTab.PaymentForm.Options.phoneNumber', { defaultValue: 'Phone Number' }),
      value: 'Phone Number',
    },
  ];

  const baseRows: PaymentUpdateRows[] = [
    {
      label: type === 'Receivable' ? vp.PaymentInfoLabel.CollectionDate : vp.PaymentInfoLabel.PaymentDate,
      id: vp.PaymentInfoId.PaymentDate,
      require: () => false,
      // Hidden while scheduling is unsupported on asp — the initial value
      // stays today, so payments always process now.
      visible: () => IS_SCHEDULING_SUPPORTED_ON_ASP,

      type: 'date',
      container: 'field',
      placeholder: '',
      preset: 'text',
      paymentRows: [],
      validate: () => Yup.string().required(),
      min: startOfDay(new Date()).getTime(),
    },
    {
      label: type === 'Receivable' ? vp.PaymentInfoLabel.CollectionCreditNotes : vp.PaymentInfoLabel.PaymentCreditNotes,
      id: vp.PaymentInfoId.CreditNotes,
      require: () => false,
      visible: () => true,
      placeholder:
        creditNotes.length === 0 || creditNotes.every((creditNote) => creditNote.remainingCredit > totalAmount)
          ? t('Shared.AccountingSystem.OpenTab.PaymentForm.noCreditsAvailable', {
              defaultValue: 'No credits available',
            })
          : t('Shared.AccountingSystem.OpenTab.PaymentForm.chooseCredits', { defaultValue: '---Choose Credits---' }),
      disabled: () =>
        creditNotes.length === 0 || creditNotes.every((creditNote) => creditNote.remainingCredit > totalAmount),
      getSelectOptions: (ctx) =>
        creditNotes.map(
          (creditNote) =>
            ({
              label: `${creditNote.currency} ${vp.formattedCurrency(String(creditNote.remainingCredit))}`,
              value: creditNote.id,
              isDisabled:
                creditNote.remainingCredit > Number(totalAmount) - Number(ctx.CreditAmount) &&
                !ctx.CreditNotes.split(',').includes(creditNote.id),
            }) as vp.SelectOption,
        ),
      type: 'multiSelect',
      container: 'field',
      preset: 'text',
      paymentRows: [],
      min: startOfDay(new Date()).getTime(),
    },

    {
      label: t('Shared.AccountingSystem.OpenTab.PaymentForm.amount', { defaultValue: 'Amount' }),
      require: () => false,
      visible: () => true,
      container: 'inputs',
      paymentRows: [
        {
          id: vp.PaymentInfoId.PaymentAmount,
          require: () => false,
          visible: () => true,
          type: 'input',
          container: 'field',
          placeholder: '',
          preset: 'currency',
          paymentRows: [],
          inputWidth: 'calc(100% - 172px)',
          getLeftElement: () => (
            <div
              className={styles.InputText}
              style={{
                paddingLeft: '16px',
                display: 'inline-block',
                width: '45px',
                position: 'relative',
                marginRight: '-11px',
              }}
            >
              {currency}
            </div>
          ),
          disabled: (ctx) => isFutureScheduled(ctx.PaymentDate),
          disabledMessage: t('Shared.AccountingSystem.OpenTab.PaymentForm.noSchedulePartialFutureDate', {
            defaultValue: 'You can not schedule a partial amount for a future date',
          }),
          validate: (ctx) =>
            Yup.number()
              .required()
              .transform((_value, originalValue: string) => Number(vp.sanitizeToNumeric(originalValue)))
              .max(
                roundCurrency(Number(totalAmount) - ctx.CreditAmount),
                t('Shared.AccountingSystem.OpenTab.PaymentForm.maxAmountError', {
                  defaultValue: `The maximum amount you can enter is ${currency} ${vp.formattedCurrency(String(roundCurrency(Number(totalAmount) - ctx.CreditAmount)))}`,
                  currency,
                  amount: vp.formattedCurrency(String(roundCurrency(Number(totalAmount) - ctx.CreditAmount))),
                }),
              )
              .test(
                'min',
                t('Shared.AccountingSystem.OpenTab.PaymentForm.amountGreaterThanZero', {
                  defaultValue: 'Amount must be greater than 0',
                }),
                (value) => (ctx.CreditAmount === 0 ? (value || 0) >= 0.01 : true),
              ),
        },
        {
          require: () => false,
          visible: () => true,
          type: 'elinx',
          container: 'field',
          paymentRows: [],
          render: ({ values }) => (
            <RemainingAmount
              totalAmount={totalAmount}
              currency={currency}
              paymentAmount={values.PaymentAmount}
              creditAmount={values.CreditAmount}
            />
          ),
        },
      ],
    },
    {
      label: vp.PaymentUpdateLabel.FinancialInstitutionNumber,
      id: vp.PaymentUpdateId.FinancialInstitutionNumber,
      require: () => false,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA',
      type: 'select',
      preset: undefined,
      showValue: true,
      getSelectOptions: () => financialInstitutions,
      renderOption: (o, { Option }) => <Option>{o.renderOption || o.label}</Option>,
      container: 'field',
      paymentRows: [],
      filterable: true,
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.BankTransitNumber,
      id: vp.PaymentUpdateId.BankTransitNumber,
      require: () => false,
      showValue: true,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA',
      type: 'select',
      container: 'field',
      paymentRows: [],
      preset: undefined,
      filterable: true,
      getSelectOptions: () => branchTransitNumbers,
      renderOption: (o, { Option }) => <Option>{o.renderOption || o.label}</Option>,
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'CA'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.RoutingNumber,
      id: vp.PaymentUpdateId.RoutingNumber,
      require: () => false,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      placeholder: '',
      preset: 'number',
      container: 'field',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.AccountNumber,
      id: vp.PaymentUpdateId.AccountNumber,
      require: () => false,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant' || ctx.PaymentMethod === 'RTP',
      type: 'input',
      placeholder: '',
      preset: 'number',
      container: 'field',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Address,
      id: vp.PaymentUpdateId.Address,
      require: () => false,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'googleAddress',
      container: 'field',
      placeholder: '',
      preset: undefined,
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      container: 'wrapper',
      require: () => false,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',

      paymentRows: [
        {
          label: vp.PaymentUpdateLabel.City,
          id: vp.PaymentUpdateId.City,
          require: () => false,
          visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
          type: 'input',
          placeholder: '',
          preset: 'text',
          container: 'field',
          paymentRows: [],
          validate: (ctx) =>
            (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
              ? Yup.string().required(
                  t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                    defaultValue: 'This value is required.',
                  }),
                )
              : Yup.string().notRequired(),
        },
        {
          label: vp.PaymentUpdateLabel.State,
          id: vp.PaymentUpdateId.State,
          require: () => false,
          visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
          type: 'input',
          placeholder: '',
          container: 'field',
          paymentRows: [],
          preset: 'text',
          validate: (ctx) =>
            (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'VoPay Instant') && country === 'US'
              ? Yup.string().required(
                  t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                    defaultValue: 'This value is required.',
                  }),
                )
              : Yup.string().notRequired(),
        },
        {
          label: vp.PaymentUpdateLabel.ZipCode,
          id: vp.PaymentUpdateId.ZipCode,
          require: () => false,
          showValue: true,
          visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
          type: 'input',
          placeholder: '',
          container: 'field',
          paymentRows: [],
          preset: 'text',
          validate: (ctx) =>
            (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
              ? Yup.string().required(
                  t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                    defaultValue: 'This value is required.',
                  }),
                )
              : Yup.string().notRequired(),
        },
      ],
    },
    {
      label: vp.PaymentUpdateLabel.IdentificationNumber,
      id: vp.PaymentUpdateId.IdentificationNumber,
      require: () => false,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      container: 'field',
      placeholder: '',
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.PhoneNumber,
      id: vp.PaymentUpdateId.PhoneNumber,
      require: () => false,
      visible: (ctx) => (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US',
      type: 'input',
      container: 'field',
      placeholder: '',
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Bank Account' || ctx.PaymentMethod === 'RTP') && country === 'US'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.InteracMethod,
      id: vp.PaymentUpdateId.InteracMethod,
      require: () => false,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'select',
      container: 'field',
      placeholder: '',
      preset: 'text',
      getSelectOptions: () => InteracMethodTypes,
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Email,
      id: vp.PaymentUpdateId.Email,
      require: () => false,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Email Address',
      type: 'input',
      container: 'field',
      placeholder: t('Shared.AccountingSystem.OpenTab.PaymentForm.provideEmailAddress', {
        defaultValue: 'Provide email address',
      }),
      preset: 'email',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Email Address'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Phone,
      id: vp.PaymentUpdateId.Phone,
      require: () => false,
      visible: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Phone Number',
      type: 'input',
      container: 'field',
      placeholder: t('Shared.AccountingSystem.OpenTab.PaymentForm.providePhoneNumber', {
        defaultValue: 'Provide phone number',
      }),
      preset: 'tel',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        ctx.InteracMethod === 'Phone Number'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Question,
      id: vp.PaymentUpdateId.Question,
      require: () => type === 'Payable',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'input',
      container: 'field',
      placeholder: t('Shared.AccountingSystem.OpenTab.PaymentForm.provideSecurityQuestion', {
        defaultValue: 'Provide security question',
      }),
      preset: 'text',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        type === 'Payable'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Answer,
      id: vp.PaymentUpdateId.Answer,
      require: () => type === 'Payable',
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'input',
      container: 'field',
      placeholder: t('Shared.AccountingSystem.OpenTab.PaymentForm.provideSecurityAnswer', {
        defaultValue: 'Provide security answer',
      }),
      preset: 'text',
      getSelectOptions: () => [],
      paymentRows: [],
      validate: (ctx) =>
        (ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request') &&
        type === 'Payable'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
    {
      label: vp.PaymentUpdateLabel.Language,
      id: vp.PaymentUpdateId.Language,
      require: () => false,
      visible: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request',
      type: 'radio',
      container: 'field',
      placeholder: '',
      preset: 'text',
      getRadioOptions: () => ({
        en: t('Shared.AccountingSystem.OpenTab.PaymentForm.english', { defaultValue: 'English' }),
        fr: t('Shared.AccountingSystem.OpenTab.PaymentForm.french', { defaultValue: 'French' }),
      }),
      paymentRows: [],
      validate: (ctx) =>
        ctx.PaymentMethod === 'Interac e-Transfer' || ctx.PaymentMethod === 'Interac e-Transfer Money Request'
          ? Yup.string().required(
              t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
                defaultValue: 'This value is required.',
              }),
            )
          : Yup.string().notRequired(),
    },
  ];

  const paymentMethodRow: PaymentUpdateRows = {
    label:
      type === 'Receivable'
        ? t('Shared.AccountingSystem.OpenTab.PaymentForm.collectFrom', { defaultValue: 'Collect From' })
        : t('Shared.AccountingSystem.OpenTab.PaymentForm.payFrom', { defaultValue: 'Pay From' }),
    id: vp.PaymentUpdateId.PaymentMethod,
    require: () => false,
    visible: (ctx) => !isElinxActive(ctx),
    container: 'field',
    type: 'select',
    showValue: false,
    placeholder: t('Shared.AccountingSystem.OpenTab.PaymentForm.addPaymentMethod', {
      defaultValue: '--Add Payment Method--',
    }),
    getSelectOptions: () =>
      country === 'US'
        ? type === 'Payable'
          ? PaymentOptionsPayablesUS
          : type === 'Receivable'
            ? PaymentOptionsReceivablesUS
            : []
        : type === 'Payable'
          ? PaymentOptionsPayables
          : type === 'Receivable'
            ? PaymentOptionsReceivables
            : [],
    renderOption: (o, args) => (
      <SelectOptionLocked {...args} featureKey={o.value ? o.value : ''}>
        {o.renderOption || o.label}
      </SelectOptionLocked>
    ),
    paymentRows: [],
    validate: (ctx) =>
      isElinxActive(ctx)
        ? Yup.string().notRequired()
        : Yup.string().required(
            t('Shared.AccountingSystem.OpenTab.PaymentForm.thisValueIsRequired', {
              defaultValue: 'This value is required.',
            }),
          ),
  };

  const checkboxRow: PaymentUpdateRows = {
    label:
      type === 'Receivable'
        ? tElinx('noDetailsLabel_Receivable', { defaultValue: "I don't have the customer's payment details" })
        : tElinx('noDetailsLabel_Payable', { defaultValue: "I don't have the supplier's payment details" }),
    subtitle:
      type === 'Receivable'
        ? tElinx('noDetailsSubtitle_Receivable', {
            defaultValue: "We'll send an eCollect request for them to enter their own banking info.",
          })
        : tElinx('noDetailsSubtitle_Payable', {
            defaultValue: "We'll send an ePay request for them to enter their own banking info.",
          }),
    id: 'IsManualEntry',
    require: () => false,
    // Hidden (not just disabled) when eLinx isn't available for this account —
    // and while eLinx is unsupported on the asp backend (see hasElinxPermission).
    visible: (ctx) => canUseElinx && !isFutureScheduled(ctx.PaymentDate),
    disabled: () => !canUseElinx,
    type: 'checkbox',
    container: 'field',
    paymentRows: [],
  };

  const isCollectLevelRow = (row: PaymentUpdateRows): boolean =>
    row.container === 'inputs' ||
    row.id === vp.PaymentUpdateId.PaymentMethod ||
    row.id === vp.PaymentInfoId.PaymentDate ||
    row.id === vp.PaymentInfoId.CreditNotes;

  const gateManual = (row: PaymentUpdateRows): PaymentUpdateRows =>
    isCollectLevelRow(row)
      ? row
      : {
          ...row,
          visible: (ctx) => row.visible(ctx) && !isElinxActive(ctx),
          validate: row.validate
            ? (ctx) => (isElinxActive(ctx) ? Yup.string().notRequired() : row.validate?.(ctx))
            : undefined,
          paymentRows: row.paymentRows.map(gateManual),
        };

  const gatedBase = baseRows.map(gateManual);
  const inputsIndex = gatedBase.findIndex((row) => row.container === 'inputs');

  return [
    ...gatedBase.slice(0, inputsIndex + 1),
    checkboxRow,
    paymentMethodRow,
    ...gatedBase.slice(inputsIndex + 1),
    ...elinxRows,
  ];
};
